import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import CategoryHero from "@/components/feature/CategoryHero";
import ProductGridCard from "@/components/feature/ProductGridCard";
import { products } from "@/mocks/products";
import { categories } from "@/mocks/categories";

type SortKey = "recentes" | "menor" | "maior" | "vendidos";

const sortOptions: { id: SortKey; label: string }[] = [
  { id: "recentes", label: "Mais recentes" },
  { id: "menor", label: "Menor preço" },
  { id: "maior", label: "Maior preço" },
  { id: "vendidos", label: "Mais vendidos" },
];

export default function NovidadesPage() {
  const [sort, setSort] = useState<SortKey>("recentes");

  const items = useMemo(() => {
    const list = products.filter((product) => product.isNew);
    switch (sort) {
      case "menor":
        return [...list].sort((a, b) => (a.promoPrice ?? a.price) - (b.promoPrice ?? b.price));
      case "maior":
        return [...list].sort((a, b) => (b.promoPrice ?? b.price) - (a.promoPrice ?? a.price));
      case "vendidos":
        return [...list].sort((a, b) => b.sold - a.sold);
      default:
        return list;
    }
  }, [sort]);

  const hero = categories.find((item) => item.id === "novidades") ?? categories[0];

  return (
    <div className="w-full">
      <CategoryHero category={hero}>
        <nav aria-label="Caminho de navegação" className="flex flex-wrap items-center gap-2 text-xs text-background-200">
          <Link to="/" className="cursor-pointer whitespace-nowrap transition-colors hover:text-secondary-300">
            Início
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line" />
          </span>
          <span className="whitespace-nowrap text-background-50">Novidades</span>
        </nav>
        <h1 className="mt-4 font-heading text-4xl font-medium text-background-50 md:text-5xl">
          Acabaram de chegar
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
          Novas peças, novas combinações e mais motivos para renovar o seu estilo.
        </p>
      </CategoryHero>

      <section className="w-full bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-700">
              <strong className="font-medium text-foreground-900">{items.length}</strong>{" "}
              {items.length === 1 ? "novidade disponível" : "novidades disponíveis"}
            </p>
            <label className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="whitespace-nowrap">Ordenar por</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
              >
                {sortOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-14">
          <div
            data-product-shop
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-6"
          >
            {items.map((product, index) => (
              <Reveal key={product.id} delay={(index % 4) * 70}>
                <ProductGridCard product={product} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}